import React, { useEffect, useState } from 'react';
import { User, Contact, MapPin, Phone, Mail, X } from 'lucide-react';
import { auth, db } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function SavedAddress({ isOpen, onClose, onAddressSelect, onAddNew, onEdit }) {
    const [savedAddresses, setSavedAddresses] = useState([]);

    useEffect(() => {
        if (!isOpen) return;

        const loadSavedAddresses = async () => {
            const user = auth.currentUser;
            if (user) {
                try {
                    let userDocRef = doc(db, "users", user.uid);
                    let docSnap = await getDoc(userDocRef);
                    if (!docSnap.exists()) {
                        userDocRef = doc(db, "users", user.email.toLowerCase());
                        docSnap = await getDoc(userDocRef);
                    }
                    if (docSnap.exists()) {
                        setSavedAddresses(docSnap.data().addresses || []);
                    }
                } catch (e) {
                    console.error("Error loading saved addresses from Firestore", e);
                }
            }
        };
        loadSavedAddresses();
    }, [isOpen]);
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 font-sans">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-[#F2F2F2] pb-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {/* Header */}
                <div className="bg-[#2c3e2b] text-white p-4 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative">
                    {/* Close button for modal */}
                    <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 hover:shadow-md hover:-translate-y-0.5 transition-all">
                        <X size={24} />
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 p-2.5 rounded-full">
                            <User size={24} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Saved Addresses</h2>
                            <p className="text-sm text-green-100 font-medium">Choose from your existing addresses</p>
                        </div>
                    </div>
                    {onAddNew && (
                        <button onClick={onAddNew} className="flex items-center gap-2 bg-white text-[#2c3e2b] px-4 py-2.5 rounded-full font-bold text-sm hover:bg-[#cdcdcd87] hover:text-[#1e1e1e] transition-colors shadow-sm mt-2 sm:mt-0 sm:mr-8 hover:shadow-md hover:-translate-y-0.5 transition-all">
                            <Contact size={18} />
                            Create a new address
                        </button>
                    )}
                </div>

                {/* Address Cards Container */}
                <div className="p-4 sm:p-8">
                    {savedAddresses.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {savedAddresses.map((address, index) => (
                                <div key={index} className="border-2 border-[#2c3e2b] bg-green-50/30 rounded-xl p-4 shadow-sm relative transition-all hover:shadow-md">
                                    {index === 0 && (
                                        <div className="absolute top-3 right-3 bg-[#2c3e2b] text-white text-xs font-bold px-2 py-1 rounded">
                                            Default
                                        </div>
                                    )}
                                    <h3 className="text-base font-bold text-gray-800 mb-3">{address.fullName}</h3>

                                    <div className="flex flex-col gap-2 text-sm text-gray-600">
                                        <div className="flex items-start gap-2">
                                            <MapPin size={16} className="text-[#2c3e2b] mt-0.5 shrink-0" />
                                            <span className="leading-snug text-xs">
                                                {address.streetAddress} {address.unitNumber && `, Unit ${address.unitNumber}`}<br />
                                                {address.city}, {address.state} {address.zipCode}<br />
                                                {address.country}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 mt-1 pt-2 border-t border-gray-200">
                                            <Phone size={14} className="text-[#2c3e2b]" />
                                            <span className="font-medium text-xs">+1 {address.mobileNumber}</span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <Mail size={14} className="text-[#2c3e2b]" />
                                            <span className="text-xs">{address.email}</span>
                                        </div>

                                        {address.gstin && (
                                            <div className="text-xs text-gray-700 font-semibold bg-[#2c3e2b]/10 px-2 py-1 rounded w-fit mt-1">
                                                GSTIN: {address.gstin}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-5 flex gap-2 w-full">
                                        {onAddressSelect && (
                                            <button
                                                onClick={() => onAddressSelect(index)}
                                                className="btn-primary flex-1 py-1.5 px-4 shadow-sm text-sm rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all">
                                                Deliver Here
                                            </button>
                                        )}
                                        {onEdit && (
                                            <button
                                                onClick={() => onEdit(index)}
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg font-medium text-sm text-gray-700 hover:bg-[#dedede] transition-colors bg-white flex items-center justify-center hover:shadow-md hover:-translate-y-0.5 transition-all"
                                            >
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                            <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                            <h3 className="text-lg font-semibold text-gray-700 mb-1">No addresses saved</h3>
                            <p className="text-gray-500 mb-6">You haven't saved any addresses yet.</p>
                            {onAddNew && (
                                <button onClick={onAddNew} className="bg-[#2c3e2b] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#1e2a1d] transition-colors shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                                    Add New Address
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
